'use client';

// BL-2734 — the mention pop-up's own countdown. Sonner's timer pauses on hover
// and in a hidden tab, but never for keyboard focus, and its pause state has no
// public API. So the pop-up stays `duration: Infinity` in Sonner and counts down
// here, pausing for all three.
//
// The pause listeners sit on Sonner's toast element (`[data-sonner-toast]`),
// not on the card inside it: that element is the one keyboard Tab lands on
// first (`tabIndex=0`), and its padding is the card's visible edge.

import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a mention pop-up stays up with nobody hovering or focused on it. */
export const MENTION_TOAST_AUTO_CLOSE_MS = 8_000;

/** Sonner's own element around a toast's content. */
const SONNER_TOAST_SELECTOR = '[data-sonner-toast]';

/** The countdown's inputs. */
export interface MentionAutoCloseOptions {
  /** Time to close, counted only while unpaused. */
  ms: number;
  /** Runs once when the countdown reaches zero. */
  onExpire: () => void;
}

/**
 * Counts `ms` down, paused while the pointer is over the pop-up, while
 * keyboard focus is inside it, or while the tab is hidden. Pausing keeps the
 * time left, so the countdown resumes rather than restarts.
 *
 * @param options - see {@link MentionAutoCloseOptions}.
 * @returns a ref callback for the card's root; the listeners bind to the
 *   Sonner toast around it (or the root itself outside a toaster).
 * @sideEffect Schedules a timeout that calls `onExpire`; listens for pointer,
 *   focus and visibility changes. All are removed on unmount.
 */
export function useMentionAutoClose({ ms, onExpire }: MentionAutoCloseOptions): (node: HTMLElement | null) => void {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const hovered = useHovered(target);
  const focused = useFocusWithin(target);
  const hidden = useDocumentHidden();
  const remainingRef = useRef(ms);
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  const paused = hovered || focused || hidden;
  useEffect(() => {
    if (paused) return;
    const startedAt = Date.now();
    const timeout = setTimeout(() => onExpireRef.current(), remainingRef.current);
    return () => {
      clearTimeout(timeout);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [paused]);

  return useCallback((node: HTMLElement | null) => {
    setTarget(node ? (node.closest<HTMLElement>(SONNER_TOAST_SELECTOR) ?? node) : null);
  }, []);
}

/**
 * Whether the pointer is over `target`.
 *
 * @param target - the element to watch, or `null`.
 * @returns `true` between its `mouseenter` and `mouseleave`.
 */
function useHovered(target: HTMLElement | null): boolean {
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (!target) return;
    const enter = () => setHovered(true);
    const leave = () => setHovered(false);
    target.addEventListener('mouseenter', enter);
    target.addEventListener('mouseleave', leave);
    return () => {
      target.removeEventListener('mouseenter', enter);
      target.removeEventListener('mouseleave', leave);
    };
  }, [target]);
  return hovered;
}

/**
 * Whether keyboard focus is on `target` or inside it.
 *
 * @param target - the element to watch, or `null`.
 * @returns `true` while focus is within it.
 */
function useFocusWithin(target: HTMLElement | null): boolean {
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!target) return;
    const focusIn = () => setFocused(true);
    const focusOut = (event: FocusEvent) => {
      // Focus moving within the pop-up (the toast, then Dismiss, then View) keeps it paused.
      if (!(event.relatedTarget instanceof Node && target.contains(event.relatedTarget))) setFocused(false);
    };
    target.addEventListener('focusin', focusIn);
    target.addEventListener('focusout', focusOut);
    return () => {
      target.removeEventListener('focusin', focusIn);
      target.removeEventListener('focusout', focusOut);
    };
  }, [target]);
  return focused;
}

/**
 * Whether the tab is in the background.
 *
 * @returns `document.hidden`, kept current.
 */
function useDocumentHidden(): boolean {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  useEffect(() => {
    const update = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  return hidden;
}
